// src/services/adjusterService.js
// Ajustador = UNA sola entidad: nombre (el mismo que aparece en cada
// reclamo y en el selector de Quick Create), foto, licencia, correo, y si
// tiene acceso al portal /claim-status. Antes esto vivía repartido en tres
// lugares (adjustersByCompany en config, adjusterProfiles, y
// authorizedAdjusters) que había que mantener sincronizados a mano — ahora
// es una sola colección `adjusters`.
//
// Restricción técnica que sí obliga a un segundo documento por debajo:
// firestore.rules necesita resolver "¿esta cuenta logueada es ajustador y
// de qué empresa?" leyendo un documento por PATH conocido — no puede hacer
// una consulta "busca el doc cuyo campo email sea X". Por eso, cuando un
// ajustador tiene acceso al portal, además de su doc en `adjusters` existe
// un puntero en `adjusterAccess/{email}` (id = su correo) que esta capa de
// servicio mantiene sincronizado automáticamente. No es una pantalla
// aparte ni algo que el admin gestione directamente.
import { initializeApp, deleteApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut
} from 'firebase/auth';
import { db, auth, firebaseConfig, USE_EMULATORS } from '../firebase/config';
import { resolveAdjusterRole, companiesOf } from '../utils/permissions';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';

const ADJUSTERS_COLLECTION = 'adjusters';
const ADJUSTER_ACCESS_COLLECTION = 'adjusterAccess';
const MIGRATION_FLAG_DOC = 'adjusterMigrationV2';

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

export const getAdjusters = async () => {
  try {
    const q = query(collection(db, ADJUSTERS_COLLECTION), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting adjusters:', error);
    return [];
  }
};

const adjusterRoleOf = role => (role === 'assistant' || role === 'csr_assistant' ? role : 'pa');

// `viewAllClaims` (P.A Admin) y `companies` (CSR Assistant, una o varias) solo
// los fija el Super Admin: las reglas de Firestore rechazan a un P.A Admin o
// a un P.A que intente crear un perfil con ellos.
export const createAdjuster = async ({ name, company, companies, license, photoDataUrl, email, notes, role, assistantOf, viewAllClaims }) => {
  try {
    const companyList = Array.isArray(companies) ? companies.filter(Boolean) : [];
    const docRef = await addDoc(collection(db, ADJUSTERS_COLLECTION), {
      name: (name || '').trim(),
      company: company || companyList[0] || '',
      ...(companyList.length ? { companies: companyList } : {}),
      license: license || '',
      photoDataUrl: photoDataUrl || '',
      email: normalizeEmail(email),
      hasPortalAccess: false,
      active: true,
      // Si es false (default), solo ve sus propios reclamos dentro de su
      // empresa; true = supervisor, ve todos los de la empresa (ver
      // firestore.rules: adjusterCanViewAll()).
      viewAllClaims: viewAllClaims === true,
      notes: notes || '',
      // 'pa', 'assistant' o 'csr_assistant': el asistente entra a los reclamos
      // de los PA de assistantOf (utils/claimAccess.js), no se asigna como PA ni
      // cobra; el CSR Assistant entra por compañía y tampoco se asigna como PA.
      role: adjusterRoleOf(role),
      assistantOf: role === 'assistant' && Array.isArray(assistantOf) ? assistantOf : [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: auth.currentUser?.email || 'system'
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating adjuster:', error);
    throw error;
  }
};

export const updateAdjuster = async (adjusterId, updates) => {
  try {
    const adjusterRef = doc(db, ADJUSTERS_COLLECTION, adjusterId);
    const payload = { ...updates, updatedAt: Timestamp.now() };
    if (typeof payload.email === 'string') payload.email = normalizeEmail(payload.email);
    await updateDoc(adjusterRef, payload);

    // Si ya tiene acceso al portal y cambió el correo, la empresa, el
    // nombre o si es supervisor, el puntero en adjusterAccess tiene que
    // reflejarlo — si no, isAdjuster()/adjusterOwnName()/adjusterCanViewAll()
    // seguirían revisando los valores viejos.
    const snap = await getDoc(adjusterRef);
    const data = snap.data();
    if (data?.hasPortalAccess && data?.email) {
      await setDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, data.email), {
        adjusterId,
        name: data.name || '',
        company: data.company || '',
        active: data.active !== false,
        viewAllClaims: Boolean(data.viewAllClaims),
        role: resolveAdjusterRole({ viewAllClaims: data.viewAllClaims }, data),
        companies: companiesOf(data)
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error updating adjuster:', error);
    throw error;
  }
};

// Crea la cuenta de Firebase Auth en una app secundaria (misma config,
// instancia aparte) para que createUserWithEmailAndPassword no reemplace la
// sesión del admin logueado ahora mismo en la app principal.
const createAuthAccount = async (email, password) => {
  const secondaryApp = initializeApp(firebaseConfig, `adjuster-signup-${Date.now()}`);
  const secondaryAuth = getAuth(secondaryApp);
  if (USE_EMULATORS) connectAuthEmulator(secondaryAuth, 'http://127.0.0.1:9099', { disableWarnings: true });
  try {
    const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    await signOut(secondaryAuth);
    return credential.user.uid;
  } finally {
    await deleteApp(secondaryApp);
  }
};

// Da acceso al portal /claim-status a un ajustador que ya existe en
// `adjusters`. Si se da password, crea su cuenta de Firebase Auth primero;
// si se deja vacío, asume que ya tiene cuenta (ej. alguien migrado desde el
// allowlist hardcodeado anterior) y solo activa el acceso.
export const grantPortalAccess = async (adjusterId, { password, parentEmail } = {}) => {
  const adjusterRef = doc(db, ADJUSTERS_COLLECTION, adjusterId);
  const snap = await getDoc(adjusterRef);
  if (!snap.exists()) throw new Error('Adjuster not found');
  const adjuster = snap.data();

  const email = normalizeEmail(adjuster.email);
  if (!email) throw new Error('El ajustador necesita un correo antes de poder darle acceso.');
  if (!adjuster.company) throw new Error('El ajustador necesita una empresa asignada antes de poder darle acceso.');

  const existingAccess = await getDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email));
  if (existingAccess.exists() && existingAccess.data().adjusterId !== adjusterId) {
    throw new Error(`${email} ya tiene acceso vinculado a otro ajustador.`);
  }

  if (password) {
    await createAuthAccount(email, password);
  }

  await setDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email), {
    adjusterId,
    name: adjuster.name || '',
    company: adjuster.company,
    active: true,
    viewAllClaims: Boolean(adjuster.viewAllClaims),
    // Rol y compañías del puntero (firestore.rules → accessRole / inMyCompanies).
    // parentEmail: quién creó a este asistente (la jerarquía la exigen las reglas).
    role: resolveAdjusterRole({ viewAllClaims: adjuster.viewAllClaims }, adjuster),
    companies: companiesOf(adjuster),
    ...(parentEmail ? { parentEmail: normalizeEmail(parentEmail) } : {}),
    mustChangePassword: Boolean(password)
  });

  await updateDoc(adjusterRef, {
    hasPortalAccess: true,
    active: true,
    updatedAt: Timestamp.now()
  });
};

// Asignar/reiniciar la contraseña de un ajustador que YA tiene acceso
// activo (a diferencia de grantPortalAccess, pensado solo para la primera
// vez). Si su email todavía no tiene cuenta de Firebase Auth (ej. se le dio
// acceso asumiendo que iba a entrar con Google y nunca llegó a hacerlo), se
// la crea con la contraseña dada. Si ya existe una cuenta (con o sin
// contraseña — p.ej. entró antes con Google), no podemos fijarle nosotros
// una contraseña sin el Admin SDK (no disponible en este proyecto) — en
// ese caso se le manda el correo oficial de Firebase para que la
// establezca él mismo.
export const ensureAdjusterPassword = async (adjusterId, password) => {
  const adjusterRef = doc(db, ADJUSTERS_COLLECTION, adjusterId);
  const snap = await getDoc(adjusterRef);
  if (!snap.exists()) throw new Error('Adjuster not found');
  const email = normalizeEmail(snap.data().email);
  if (!email) throw new Error('El ajustador necesita un correo antes de poder asignarle contraseña.');

  try {
    await createAuthAccount(email, password);
    await setDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email), { mustChangePassword: true }, { merge: true });
    return { method: 'created' };
  } catch (error) {
    if (error.code !== 'auth/email-already-in-use') throw error;
    await sendPasswordResetEmail(auth, email);
    return { method: 'reset_email' };
  }
};

export const revokePortalAccess = async (adjusterId) => {
  const adjusterRef = doc(db, ADJUSTERS_COLLECTION, adjusterId);
  const snap = await getDoc(adjusterRef);
  const email = normalizeEmail(snap.data()?.email);
  if (email) {
    await updateDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email), { active: false });
  }
  await updateDoc(adjusterRef, { active: false, updatedAt: Timestamp.now() });
};

export const restorePortalAccess = async (adjusterId) => {
  const adjusterRef = doc(db, ADJUSTERS_COLLECTION, adjusterId);
  const snap = await getDoc(adjusterRef);
  const email = normalizeEmail(snap.data()?.email);
  if (email) {
    await updateDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email), { active: true });
  }
  await updateDoc(adjusterRef, { active: true, updatedAt: Timestamp.now() });
};

// Borra el ajustador y, si tenía acceso, su puntero. No borra la cuenta de
// Firebase Auth (requeriría Admin SDK, que este proyecto no tiene) — la
// persona podría seguir autenticándose, pero sin el puntero isAdjuster()
// da false y firestore.rules le corta el acceso a todo.
export const deleteAdjuster = async (adjusterId) => {
  const adjusterRef = doc(db, ADJUSTERS_COLLECTION, adjusterId);
  const snap = await getDoc(adjusterRef);
  const email = normalizeEmail(snap.data()?.email);
  await deleteDoc(adjusterRef);
  if (email) {
    await deleteDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email)).catch(() => {});
  }
};

// ============================================================
// MIGRACIÓN ÚNICA desde el modelo anterior (adjustersByCompany en config,
// adjusterProfiles, authorizedAdjusters) hacia `adjusters`. Idempotente:
// se puede llamar muchas veces, solo hace algo la primera vez (chequea el
// flag en config/adjusterMigrationV2 y además nunca duplica por
// nombre+empresa ya existente).
// ============================================================
export const migrateLegacyAdjusterData = async () => {
  const flagRef = doc(db, 'config', MIGRATION_FLAG_DOC);
  const flagSnap = await getDoc(flagRef);
  if (flagSnap.exists() && flagSnap.data().done) return { migrated: false };

  try {
    const existing = await getAdjusters();
    const existingByKey = new Map(existing.map(a => [`${a.name}::${a.company}`, a]));
    const existingByName = new Map();
    existing.forEach(a => {
      if (!existingByName.has(a.name)) existingByName.set(a.name, []);
      existingByName.get(a.name).push(a);
    });

    // 1) Nombres desde config/claims_settings.adjustersByCompany
    const settingsRef = doc(db, 'config', 'claims_settings');
    const settingsSnap = await getDoc(settingsRef);
    const adjustersByCompany = settingsSnap.exists() ? (settingsSnap.data().adjustersByCompany || {}) : {};

    for (const [company, names] of Object.entries(adjustersByCompany)) {
      for (const name of (names || [])) {
        const key = `${name}::${company}`;
        if (existingByKey.has(key)) continue;
        const newId = await createAdjuster({ name, company, license: '', photoDataUrl: '', email: '', notes: '' });
        const created = { id: newId, name, company, license: '', photoDataUrl: '', email: '', hasPortalAccess: false, active: true };
        existingByKey.set(key, created);
        if (!existingByName.has(name)) existingByName.set(name, []);
        existingByName.get(name).push(created);
      }
    }

    // 2) Perfiles (foto/licencia/correo) desde adjusterProfiles, emparejados por nombre
    const profilesSnap = await getDocs(collection(db, 'adjusterProfiles'));
    for (const profileDoc of profilesSnap.docs) {
      const profile = profileDoc.data();
      const candidates = existingByName.get(profile.name) || [];
      if (candidates.length === 1) {
        await updateAdjuster(candidates[0].id, {
          license: profile.license || candidates[0].license || '',
          photoDataUrl: profile.photoDataUrl || candidates[0].photoDataUrl || '',
          email: profile.email || candidates[0].email || ''
        });
      } else if (candidates.length === 0) {
        // Perfil huérfano: el nombre ya no está en ninguna empresa activa.
        // Se migra igual (no se pierde info), con nota para que el admin
        // le asigne una empresa.
        const newId = await createAdjuster({
          name: profile.name,
          company: '',
          license: profile.license || '',
          photoDataUrl: profile.photoDataUrl || '',
          email: profile.email || '',
          notes: 'Migrado automáticamente — asigna una empresa.'
        });
        existingByName.set(profile.name, [{ id: newId, name: profile.name }]);
      }
      // Si hay más de un candidato (mismo nombre en más de una empresa), se
      // deja para que el admin lo revise a mano en vez de adivinar cuál.
    }

    // 3) Acceso al portal desde la colección vieja authorizedAdjusters
    //    (email -> company/active), emparejado por empresa cuando hay un
    //    único ajustador sin acceso todavía bajo esa empresa.
    const legacyAccessSnap = await getDocs(collection(db, 'authorizedAdjusters'));
    for (const accessDoc of legacyAccessSnap.docs) {
      const legacy = accessDoc.data();
      const email = normalizeEmail(legacy.email || accessDoc.id);
      if (!email || !legacy.company) continue;

      const candidatesInCompany = (await getAdjusters())
        .filter(a => a.company === legacy.company && !a.hasPortalAccess);

      let targetId;
      if (candidatesInCompany.length === 1) {
        targetId = candidatesInCompany[0].id;
        await updateAdjuster(targetId, { email });
      } else {
        // Cero o varios candidatos ambiguos — se crea un registro nuevo con
        // el usuario local del correo como nombre provisional, marcado para
        // que el admin lo corrija con el nombre real.
        targetId = await createAdjuster({
          name: email.split('@')[0],
          company: legacy.company,
          license: '',
          photoDataUrl: '',
          email,
          notes: 'Migrado automáticamente desde el acceso anterior — verifica que el nombre calce con el usado en los reclamos.'
        });
      }

      await setDoc(doc(db, ADJUSTER_ACCESS_COLLECTION, email), {
        adjusterId: targetId,
        company: legacy.company,
        active: legacy.active !== false
      });
      await updateDoc(doc(db, ADJUSTERS_COLLECTION, targetId), {
        hasPortalAccess: true,
        active: legacy.active !== false,
        updatedAt: Timestamp.now()
      });
    }

    await setDoc(flagRef, { done: true, migratedAt: Timestamp.now() });
    return { migrated: true };
  } catch (error) {
    console.error('Error migrating legacy adjuster data:', error);
    return { migrated: false, error };
  }
};

// Renombrar un ajustador: los reclamos, cheques, citas, closing statements y
// deudas guardan el nombre como texto (y las reglas de Firestore comparan
// por nombre), así que cambiarlo solo en el perfil le quitaría el acceso a
// todo lo suyo. Esto reemplaza el nombre en todos esos lugares, en listas
// de acceso y en los asistentes enlazados. No toca texto histórico (p. ej.
// "payable to" de cheques) ni la colección vieja congelada `claims`.
// Devuelve cuántos documentos cambió.
export const renameAdjusterEverywhere = async (oldName, newName) => {
  const from = (oldName || '').trim();
  const to = (newName || '').trim();
  if (!from || !to || from === to) return 0;
  const swap = list => (Array.isArray(list) && list.includes(from) ? list.map(n => (n === from ? to : n)) : null);
  const writes = [];
  const scan = async (collectionName, fields) => {
    const snapshot = await getDocs(collection(db, collectionName));
    snapshot.docs.forEach(item => {
      const data = item.data();
      const changes = {};
      fields.forEach(field => {
        const value = data[field];
        if (value === from) changes[field] = to;
        const swapped = swap(value);
        if (swapped) changes[field] = swapped;
      });
      if (Object.keys(changes).length > 0) writes.push({ ref: item.ref, changes });
    });
  };
  await scan('claimRecords', ['adjusterName', 'collaboratorAdjusters', 'otherAccess', 'accessNames']);
  await scan('checks', ['adjusterName', 'accessNames']);
  await scan('claimEvents', ['adjusterName', 'accessNames']);
  await scan('closingStatements', ['adjusterName']);
  await scan('operatorDebts', ['adjusterName']);
  await scan(ADJUSTERS_COLLECTION, ['assistantOf']);

  const settingsRef = doc(db, 'config', 'claims_settings');
  const settings = await getDoc(settingsRef);
  const byCompany = settings.exists() ? settings.data().adjustersByCompany : null;
  if (byCompany) {
    const next = Object.fromEntries(Object.entries(byCompany).map(([company, names]) => [company, swap(names) || names]));
    if (JSON.stringify(next) !== JSON.stringify(byCompany)) writes.push({ ref: settingsRef, changes: { adjustersByCompany: next } });
  }

  for (let i = 0; i < writes.length; i += 400) {
    const batch = writeBatch(db);
    writes.slice(i, i + 400).forEach(w => batch.update(w.ref, w.changes));
    await batch.commit();
  }
  return writes.length;
};
